/**
 * #106: user-selected AI provider ("source"), persisted in browser localStorage.
 * When set to a concrete provider it's attached as the `X-LLM-Provider` header
 * (see services/chatapi.js) and the backend uses it to override the server-
 * configured provider for that request. Empty string => use the server default.
 *
 * Pairs with the user's own key (utils/llmApiKey.js, `X-LLM-API-Key`).
 */
const STORAGE_KEY = 'ai_provider';

// The providers a user can pick from the UI. '' = server default (no override).
// #1042/#778: GitHub Models was retired as a selectable source. A user can no
// longer choose it fresh; a returning user's browser that still holds it from
// before the retirement gets it migrated to '' on read — see getAiProvider()
// below (#1043).
export const AI_PROVIDERS = [
  { id: '', label: 'Server default' },
  { id: 'openrouter', label: 'OpenRouter' },
];

export const AI_PROVIDER_CHANGED_EVENT = 'ai-provider-changed';

export function getAiProvider() {
  try {
    if (typeof window === 'undefined') return '';
    const stored = window.localStorage.getItem(STORAGE_KEY) || '';
    // #1042/#778 retired 'github_models' as a selectable source (it's no longer
    // in AI_PROVIDERS), but a returning user's browser can still hold it from
    // before the retirement. Left as-is, this would keep returning the stale
    // id forever -- chatapi.js sends it as X-LLM-Provider on every request --
    // while every UI surface that maps the id through AI_PROVIDERS (e.g.
    // ChatInterface's activeProviderLabel) finds no match and falls back to
    // "Server default": the UI reports one source, requests use another.
    // Migrate on read -- a stored id that isn't currently selectable is
    // treated as, and rewritten to, '' (server default) -- so every reader
    // agrees. This is only the provider-choice value; it does not touch the
    // separate saved-API-key store (utils/llmApiKey.js / the backend-held
    // org/user keys), so a saved github_models key row still renders and
    // stays deletable (#1042's own scope).
    if (stored && !AI_PROVIDERS.some((p) => p.id === stored)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return '';
    }
    return stored;
  } catch {
    return '';
  }
}

export function setAiProvider(provider) {
  try {
    if (provider) window.localStorage.setItem(STORAGE_KEY, provider);
    else window.localStorage.removeItem(STORAGE_KEY);
    // Notify listeners (e.g. the model dropdown) to re-fetch for the new provider.
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(AI_PROVIDER_CHANGED_EVENT, { detail: provider }));
    }
  } catch { /* localStorage unavailable */ }
}
